import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ProdutoFabricante, Produto, Login, FuncaoService, GlobalService, FavorecidoService, EddyAutoComplete, BaseNacional } from 'eddydata-lib';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { ProdutoFabricanteService } from '../service/produto-fabricante.service';
import { takeUntil } from 'rxjs/operators';
import { BaseNacionalService } from '../service/base-nacional.service';
import * as toastr from 'toastr';
import { MovimentoItemEstoqueService } from 'administrativo-lib';

@Component({
    selector: 'lib-produto-fabricante',
    templateUrl: './produto-fabricante.component.html'
})
export class ProdutoFabricanteComponent implements OnInit, OnDestroy {

    public itemAtual: ProdutoFabricante;
    protected unsubscribe: Subject<void> = new Subject();
    public baseNacionalAutoComplete: EddyAutoComplete<BaseNacional>;
    private _tipo: string;

    @Input() lista: ProdutoFabricante[] = [];
    @Output() listaChange: EventEmitter<any> = new EventEmitter();
    @Input() entidade: Produto;
    @Input() login: Login;
    @Input()
    set tipo(valor: string) {
        this._tipo = valor;
        this.alterarAutoComplete();
    }
    get tipo(): string {
        return this._tipo;
    }

    @ViewChild('btnAdicionar') public btnAdicionar: ElementRef;

    constructor(
        private messageService: MessageService,
        protected funcaoService: FuncaoService,
        protected globalService: GlobalService,
        protected produtoFabricanteService: ProdutoFabricanteService,
        protected favorecidoService: FavorecidoService,
        protected baseNacionalService: BaseNacionalService,
        protected movimentoItemService: MovimentoItemEstoqueService
    ) { }

    ngOnInit() {
        this.alterarAutoComplete();
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public alterarAutoComplete() {
        this.baseNacionalAutoComplete = new EddyAutoComplete(null, this.baseNacionalService,
            'id', ['codigo', 'decricao'], { tipo: this._tipo }, { text: ['codigo', 'descricao'] }, null, 20, (entidade) => {
                return `${entidade.codigo} - ${entidade.descricao}`;
            }
        );
    }

    public adicionar() {
        this.itemAtual = new ProdutoFabricante();
        this.itemAtual['editavel'] = true;
        this.itemAtual.fabricante_internacional = false;
        this.lista.unshift(this.itemAtual);
        this.btnAdicionar.nativeElement.disabled = true;
        this.change();
    }

    public salvar(item: ProdutoFabricante) {
        try {
            if (!item.nome_fantasia) {
                throw new Error('Informe o nome fantasia do fabricante!');
            }
            if (!item.fabricante_internacional && !item.fabricante_base_nacional) {
                throw new Error('Informe o CNPJ do fabricante nacional!');
            }
            if (!item.material_base_nacional) {
                throw new Error('Informe o material!');
            }
            if (!item.tipo) {
                throw new Error('Informe o tipo!');
            }
            if (item.registro_anvisa && item.registro_anvisa?.length != 13) {
                throw new Error(`O campo registro ANVISA deve conter exatamente 13 dígitos, mas contém ${item.registro_anvisa?.length}.`);
            }
            this.itemAtual['editavel'] = false;
            this.btnAdicionar.nativeElement.disabled = false;
            this.change();
        } catch (e) {
            this.messageService.add({ severity: 'error', summary: 'Validação', detail: e.message });
            throw e;
        }
    }

    public editar(item: ProdutoFabricante) {
        this.itemAtual = item;
        this.itemAtual['editavel'] = true;
        this.btnAdicionar.nativeElement.disabled = true;
        this.change();
    }

    public cancelar(item: ProdutoFabricante) {
        this.itemAtual = item;
        if (!this.itemAtual.id) {
            this.lista.splice(this.lista.indexOf(item), 1);
        } else {
            this.itemAtual['editavel'] = false;
        }
        this.btnAdicionar.nativeElement.disabled = false;
        this.change();
    }

    public async remover(item: ProdutoFabricante) {
        if (item.id) {
            const movimento = await this.movimentoItemService.filtrar(0, -1, { 'produto_fabricante.id': item.id }).toPromise();

            if (movimento.content.length > 0) {
                toastr.warning('A classificação esta vinculado a diversas entradas, não é possivel removê-lo.')
                return;
            }

            this.produtoFabricanteService.remover(item.id).pipe(takeUntil(this.unsubscribe))
                .subscribe((res) => {
                    this.messageService.add({ severity: 'info', summary: 'Exclusão', detail: 'Registro removido com sucesso!' });
                    this.lista = this.lista.filter(itm => itm.id !== item.id);
                    this.btnAdicionar.nativeElement.disabled = false;
                    this.change();
                }, error => {
                    console.log(error)
                    this.messageService.add({ severity: 'error', summary: 'Exclusão', detail: error?.error?.payload });
                });
        }
        this.lista.splice(this.lista.indexOf(item), 1);
        this.btnAdicionar.nativeElement.disabled = false;
        this.change();
    }

    private change() {
        this.listaChange.emit(this.lista);
    }

    public retornarTipo(item: ProdutoFabricante): string {
        if (!item || !item.tipo) {
            return 'Não informado';
        }
        switch (item.tipo) {
            case 'B':
                return 'Componente Básico da Assistência Farmacêutica';
            case 'E':
                return 'Componente Especializado da Assistência Farmacêutica';
            case 'S':
                return 'Componente Estratégico da Assistência Farmacêutica';
            case 'O':
                return 'Outros';
            default:
                return 'Não informado';
        }
    }

    public buscarReceitaFederal(item: ProdutoFabricante) {
        if (!item.fabricante_base_nacional || item.fabricante_base_nacional.length < 14) return;
        let cnpj = item.fabricante_base_nacional
        cnpj = cnpj.replace(/[.]*[-]*[(]*[)]*[\/]*/g, '');
        this.favorecidoService
            .buscarReceitaWS(cnpj, this.login.cidade.id).pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
                if (!data) {
                    return;
                }
                item.nome_fantasia = (data.nome_fantasia ?? '') !== '' ? data.nome_fantasia : data.nome;
            }, error => {
                toastr.warning(error.error.payload)
            });
    }
}
